import { useState, useCallback } from "react";
import { MoreHorizontal, MapPin, ChevronLeft, ChevronRight } from "lucide-react";
import type { SocialPost } from "@/types";
import { ActionBar } from "./action-bar";
import { cn } from "@/lib/utils";

interface FeedCardProps {
  post: SocialPost;
}

import { readMetric, METRIC_KEYS } from "@/lib/api";

/* ---------- Raw metrics helpers: read what the platform gave us, nothing more ---------- */
function getLikeCount(raw: unknown): number | null {
  return readMetric(raw, METRIC_KEYS.likes);
}

function getCommentCount(raw: unknown): number | null {
  return readMetric(raw, METRIC_KEYS.comments);
}

/* ---------- Sub-components ---------- */

function PostHeader({ post }: { post: SocialPost }) {
  const account = post.socialAccounts[0];
  return (
    <div className="flex items-center justify-between px-4 py-3">
      <div className="flex items-center gap-3">
        {/* Avatar */}
        <div className="relative w-9 h-9 rounded-full overflow-hidden bg-muted ring-[0.5px] ring-border">
          {account?.profilePhotoUrl ? (
            <img
              src={account.profilePhotoUrl}
              alt={account.username || ""}
              className="w-full h-full object-cover"
              loading="lazy"
            />
          ) : (
            <div className="w-full h-full bg-gradient-to-br from-muted-foreground/20 to-muted-foreground/10 flex items-center justify-center">
              <span className="text-xs font-semibold uppercase text-muted-foreground">
                {(account?.username || post.platform).charAt(0)}
              </span>
            </div>
          )}
        </div>

        {/* Username + platform */}
        <div className="flex flex-col">
          <span className="text-[13px] font-semibold text-foreground leading-tight">
            {account?.username || post.platform}
          </span>
          {post.platformUrl && (
            <span className="text-[11px] text-muted-foreground leading-tight flex items-center gap-0.5">
              <MapPin className="w-2.5 h-2.5" />
              {new URL(post.platformUrl).hostname.replace("www.", "")}
            </span>
          )}
        </div>
      </div>

      <button
        className="p-1 -mr-1 text-muted-foreground hover:text-foreground transition-colors"
        aria-label="More options"
      >
        <MoreHorizontal className="w-5 h-5" />
      </button>
    </div>
  );
}

function PostMedia({ post }: { post: SocialPost }) {
  const [currentIndex, setCurrentIndex] = useState(0);
  const mediaItems = post.media.length > 0 ? post.media : [];

  if (mediaItems.length === 0) {
    return (
      <div className="aspect-square bg-muted flex items-center justify-center">
        <span className="text-sm text-muted-foreground">No media</span>
      </div>
    );
  }

  const item = mediaItems[currentIndex];
  const isVideo = item?.type === "video";
  const hasMultiple = mediaItems.length > 1;

  const goPrev = (e: React.MouseEvent) => {
    e.stopPropagation();
    setCurrentIndex((i) => (i > 0 ? i - 1 : mediaItems.length - 1));
  };
  const goNext = (e: React.MouseEvent) => {
    e.stopPropagation();
    setCurrentIndex((i) => (i < mediaItems.length - 1 ? i + 1 : 0));
  };

  return (
    <div className="relative bg-black w-full select-none">
      {/* Media container — 4:5 feed aspect */}
      <div className="relative w-full aspect-[4/5]">
        {isVideo ? (
          <video
            src={item.url}
            className="w-full h-full object-cover"
            muted
            loop
            playsInline
            autoPlay
            preload="metadata"
          />
        ) : (
          <img
            src={item?.url}
            alt={post.caption}
            className="w-full h-full object-cover"
            loading="lazy"
          />
        )}

        {/* Carousel arrows (desktop hover) */}
        {hasMultiple && (
          <>
            <button
              onClick={goPrev}
              className="absolute left-2 top-1/2 -translate-y-1/2 p-1.5 rounded-full bg-black/30 text-white/80 hover:bg-black/50 opacity-0 hover:opacity-100 focus:opacity-100 transition-opacity"
              aria-label="Previous media"
            >
              <ChevronLeft className="w-5 h-5" />
            </button>
            <button
              onClick={goNext}
              className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 rounded-full bg-black/30 text-white/80 hover:bg-black/50 opacity-0 hover:opacity-100 focus:opacity-100 transition-opacity"
              aria-label="Next media"
            >
              <ChevronRight className="w-5 h-5" />
            </button>
          </>
        )}
      </div>

      {/* Dot indicators */}
      {hasMultiple && (
        <>
          <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-1.5">
            {mediaItems.map((_, i) => (
              <button
                key={i}
                onClick={() => setCurrentIndex(i)}
                className={cn(
                  "h-1.5 rounded-full transition-all duration-200",
                  i === currentIndex ? "w-4 bg-white" : "w-1.5 bg-white/50",
                )}
                aria-label={`Go to media ${i + 1}`}
              />
            ))}
          </div>
          <div className="absolute bottom-0 left-0 right-0 h-16 bg-gradient-to-t from-black/40 to-transparent pointer-events-none" />
        </>
      )}
    </div>
  );
}

function PostCaption({ post }: { post: SocialPost }) {
  const account = post.socialAccounts[0];
  const [expanded, setExpanded] = useState(false);

  const shouldTruncate = post.caption.length > 120;
  const displayCaption =
    shouldTruncate && !expanded ? post.caption.slice(0, 120) + "..." : post.caption;

  return (
    <div className="px-4 pb-2">
      <p className="text-[13px] text-foreground leading-relaxed">
        <span className="font-semibold mr-1">{account?.username || post.platform}</span>
        {displayCaption}
      </p>
      {shouldTruncate && (
        <button
          onClick={() => setExpanded(!expanded)}
          className="text-[13px] text-muted-foreground hover:text-foreground transition-colors mt-0.5"
        >
          {expanded ? "Show less" : "more"}
        </button>
      )}
    </div>
  );
}

export function FeedCard({ post }: FeedCardProps) {
  const [liked, setLiked] = useState(false);

  const handleLike = useCallback(() => {
    setLiked((l) => !l);
  }, []);

  // Use what the worker actually gave us — no cross-platform comparison
  const rawLikes = getLikeCount(post.rawMetrics);
  const rawComments = getCommentCount(post.rawMetrics);
  const likes = (rawLikes ?? 0) + (liked ? 1 : 0);
  const comments = rawComments ?? 0;

  return (
    <article className="w-full border-b border-border/50 pb-4 animate-in slide-in-from-bottom-2 duration-300">
      <PostHeader post={post} />
      <PostMedia post={post} />

      <div className="px-4">
        <ActionBar
          likes={likes}
          comments={comments}
          liked={liked}
          onLike={handleLike}
          onComment={() => {}}
          onShare={() => {}}
        />
      </div>

      <PostCaption post={post} />

      {/* Timestamp */}
      <div className="px-4 pt-1">
        <span className="text-[10px] text-muted-foreground uppercase tracking-wide">
          {new Date(post.createdAt).toLocaleDateString("en-US", {
            month: "long",
            day: "numeric",
          })}
        </span>
      </div>
    </article>
  );
}

/* ======================= EXPLORE GRID CARD ======================= */

interface ExploreCardProps {
  post: SocialPost;
  onClick: () => void;
}

function formatCount(n: number): string {
  if (n >= 1_000_000) return (n / 1_000_000).toFixed(1) + "M";
  if (n >= 1_000) return (n / 1_000).toFixed(1) + "K";
  return n.toLocaleString();
}

export function ExploreCard({ post, onClick }: ExploreCardProps) {
  const isVideo = post.media[0]?.type === "video";

  // Show likes only if platform actually reported them
  const likes = getLikeCount(post.rawMetrics);

  return (
    <button
      onClick={onClick}
      className="relative aspect-square w-full bg-muted overflow-hidden group focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2"
    >
      {post.media[0]?.url ? (
        <img
          src={post.media[0].url}
          alt={post.caption}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
          loading="lazy"
        />
      ) : (
        <div className="w-full h-full bg-muted flex items-center justify-center">
          <span className="text-xs text-muted-foreground">No image</span>
        </div>
      )}

      {/* Hover overlay — show raw metric if available */}
      <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-colors duration-200 hidden sm:flex items-center justify-center gap-5 opacity-0 group-hover:opacity-100">
        {likes !== null && (
          <div className="flex items-center gap-1.5 text-white text-sm font-semibold">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" stroke="none">
              <path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z" />
            </svg>
            <span>{formatCount(likes)}</span>
          </div>
        )}
      </div>

      {/* Video indicator */}
      {isVideo && (
        <div className="absolute top-2 right-2">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="white" className="drop-shadow-lg">
            <polygon points="8 5 19 12 8 19" />
          </svg>
        </div>
      )}

      {/* Multiple images indicator */}
      {post.media.length > 1 && (
        <div className="absolute top-2 right-2">
          <svg
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            stroke="white"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            className="drop-shadow-lg"
          >
            <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
            <line x1="9" y1="3" x2="9" y2="21" />
          </svg>
        </div>
      )}
    </button>
  );
}
